<template>
  <CustomModal ref="modal" title="Rattacher un achat" class="link-purchase-modal">
    <div class="container">
      <p v-if="order" class="intro">Choisissez l'achat (facture ou avoir fournisseur) à rattacher au <strong>dossier N°{{ order.number }}</strong><template v-if="order.title"> - {{ order.title }}</template>.</p>

      <div class="search-box">
        <span class="fas fa-magnifying-glass"></span>
        <input v-model="selfSearch" type="search" placeholder="Fournisseur, référence, libellé..." />
      </div>

      <ul v-if="visibleCandidates.length > 0" class="candidates">
        <li v-for="candidate in visibleCandidates" :key="candidate.id" class="candidate" :class="{selected: candidate.id === selectedId}" @click="select(candidate)">
          <span class="radio"></span>
          <div class="candidate-main">
            <div class="candidate-top">
              <strong>{{ candidate.supplierName || 'Fournisseur' }}</strong>
              <span class="candidate-label" :title="candidate.label">{{ title(candidate) }}</span>
            </div>
            <div class="candidate-meta">
              <span>{{ formatDate(candidate.invoiceDate) }}</span>
              <StatusBadge :status="candidate.status" :status-enum="statusEnumOf(candidate)" />
              <span v-if="isCreditNote(candidate)" class="partial">avoir</span>
              <span v-if="orderCount(candidate) > 0" class="partial" title="Déjà ventilé sur d'autres dossiers">déjà ventilé sur {{ orderCount(candidate) }} dossier{{ orderCount(candidate) > 1 ? 's' : '' }}</span>
            </div>
          </div>
          <div class="candidate-amount">
            <strong>{{ formatAmount(candidate.available) }}</strong>
            <span>à ventiler sur {{ formatAmount(candidate.htAmount) }} HT</span>
          </div>
        </li>
      </ul>
      <p v-else class="empty">{{ candidates.length === 0 ? "Aucun achat n'est disponible : ils sont tous déjà rattachés à ce dossier ou entièrement ventilés sur d'autres dossiers." : 'Aucun achat ne correspond à la recherche.' }}</p>

      <CustomForm ref="form" :inputs=inputs :submit=buttons.submit @submit="preventNativeSubmit">
        <template v-if="detail">
          <AllocationEditor ref="allocationEditor" :fixed-order-id="orderId" :lines="detail.lines" :total-ht="selected.available" />
          <p class="hint">Reste à ventiler sur cet achat : {{ formatAmount(selected.available) }} HT. Le reste peut être ventilé sur d'autres dossiers.</p>
        </template>
        <div class="button-container">
          <CustomButton class="button" @click="submitForm" :params=submitParams />
          <CustomButton class="button" :is-secondary="true" @click="closeModal()" :params=buttons.cancel />
        </div>
      </CustomForm>
    </div>
  </CustomModal>
</template>

<script>
import {library} from '@fortawesome/fontawesome-svg-core';
import {faMagnifyingGlass} from "@fortawesome/free-solid-svg-icons";
import {NotificationService} from "@vues/v-common";
import CustomButton from "@/components/forms/customs/customButton/CustomButton.vue";
import CustomForm from "@/components/forms/CustomForm.vue";
import CustomModal from "@/components/modals/CustomModal.vue";
import StatusBadge from "@/components/tables/commons/StatusBadge.vue";
import AllocationEditor from "@/purchase/components/AllocationEditor.vue";
import {PurchaseController} from "@/purchase/controllers/PurchaseController";
import {formatAmount, formatDate, isCreditNote, statusEnumOf} from "@/purchase/utils";
import {allocationsFor, allocationValue, remainingOf, toAllocationPayload, unallocatedHt} from "@/purchase/allocations";
import {ALLOCATION_METHOD_ENUM} from "@/purchase/utils";
library.add(faMagnifyingGlass);

const normalize = text => String(text ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

// Modale de rattachement d'un achat existant à un dossier : on choisit l'achat puis sa part dans le dossier, saisie avec le même
// éditeur de ventilation que l'achat (par défaut tout ce qui n'est pas encore ventilé sur un autre dossier, article par article).
// callback est appelé après le rattachement.
export default {
  name: 'LinkPurchaseModal',
  props: ['callback'],
  components: {CustomModal, CustomForm, CustomButton, StatusBadge, AllocationEditor},
  data() {
    return {
      order: undefined,
      candidates: [],
      selectedId: undefined,
      detail: undefined,      // achat sélectionné, avec ses articles
      selfSearch: '',
      inputs: {},
      buttons: {
        submit: {text: "Rattacher"},
        cancel: {text: "Annuler"},
      },
      isSaving: false,
    };
  },
  computed: {
    selected() {
      return this.candidates.find(candidate => candidate.id === this.selectedId);
    },
    orderId() {
      return this.order ? String(this.order.id) : undefined;
    },
    visibleCandidates() {
      const search = normalize(this.selfSearch.trim());
      return this.candidates.filter(candidate => !search || normalize([candidate.supplierName, candidate.reference, candidate.label].join(' ')).includes(search));
    },
    submitParams() {
      return {...this.buttons.submit, disabled: !this.detail || this.isSaving};
    },
  },
  methods: {
    formatAmount,
    formatDate,
    isCreditNote,
    statusEnumOf,
    // Dossiers sur lesquels l'achat est déjà ventilé (un dossier peut avoir une part par article)
    orderCount(candidate) {
      return new Set((candidate.allocations ?? []).map(allocation => String(allocation.orderId))).size;
    },
    title(candidate) {
      return [candidate.reference, candidate.label].filter((part, index, parts) => part && parts.indexOf(part) === index).join(' - ');
    },
    // Achats qui ne sont pas déjà dans le dossier et dont une partie reste à ventiler, les plus récents d'abord
    openModal({order, purchases}) {
      this.order = order;
      this.selfSearch = '';
      this.selectedId = undefined;
      this.detail = undefined;
      this.candidates = purchases
          .filter(purchase => allocationsFor(purchase, order.id).length === 0)
          .map(purchase => ({...purchase, available: unallocatedHt(purchase)}))
          .filter(purchase => purchase.available > 0.005)
          .sort((a, b) => new Date(b.invoiceDate) - new Date(a.invoiceDate));
      this.$refs.modal.openModal();
    },
    closeModal(event = false) {
      this.$refs.modal?.closeModal(event);
    },
    preventNativeSubmit(event) {
      event?.preventDefault?.();
    },
    // Charge les articles de l'achat, puis propose d'office tout ce qui n'est pas encore ventilé ailleurs (par article s'il y en a)
    async select(candidate) {
      this.selectedId = candidate.id;
      this.detail = undefined;
      const detail = await PurchaseController.get(candidate.id);
      if (!detail || detail.errors || this.selectedId !== candidate.id) return;

      this.detail = {...detail, lines: detail.lines ?? [], allocations: detail.allocations ?? []};
      await this.$nextTick();
      const editor = this.$refs.allocationEditor;
      editor.reset();
      if (this.detail.lines.length === 0) return editor.addRow({method: ALLOCATION_METHOD_ENUM.AMOUNT.value, value: candidate.available});

      this.detail.lines.forEach(line => {
        const rows = this.detail.allocations
            .filter(allocation => allocation.lineId === line.id)
            .map(allocation => ({method: allocation.method, value: allocationValue(allocation)}));
        const remaining = remainingOf({rows, line, totalHt: this.detail.htAmount}, true);
        if (remaining > 0) editor.addRow({lineId: line.id, method: ALLOCATION_METHOD_ENUM.QUANTITY.value, value: remaining});
      });
    },
    async submitForm() {
      if (!this.detail) return;
      const mine = this.$refs.allocationEditor.getValue();
      if (mine === undefined) return;
      if (mine.length === 0) {
        (new NotificationService).addNotification("danger", "Indiquez la part de cet achat dans le dossier");
        return;
      }

      // Les parts des autres dossiers sont renvoyées telles quelles
      const others = this.detail.allocations.filter(allocation => String(allocation.orderId) !== this.orderId).map(toAllocationPayload);
      const allocations = [...others, ...mine];
      this.isSaving = true;
      const res = await this.$refs.form.submitForm(() => PurchaseController.patch(this.selectedId, {allocations}));
      this.isSaving = false;
      if (!res.errors) {
        (new NotificationService).addNotification("success", "Achat rattaché au dossier");
        this.closeModal(true);
        if (this.callback) this.callback();
      }
    },
  },
}
</script>

<style lang="stylus" scoped>
@require '~/src/common/style/common.styl';

.container
  display flex
  flex-direction column
  gap 10px

.intro
  margin 0
  font-size 14px
  color #4a5670

.search-box
  display flex
  align-items center
  gap 8px
  padding 0 12px
  height 38px
  background CTRL_BG
  border 1px solid CTRL_BORDER
  border-radius CTRL_RADIUS

  &:focus-within
    background CTRL_BG_FOCUS
    border-color CTRL_FOCUS
    box-shadow 0 0 0 3px CTRL_FOCUS_RING

  span
    color CTRL_PLACEHOLDER
    font-size 13px

  input
    flex 1
    min-width 0
    border none
    outline none
    background transparent
    font-size 14px

.candidates
  max-height 300px
  margin 0
  padding 4px
  list-style none
  overflow-y auto
  border 1px solid #e3e7ef
  border-radius 12px

.candidate
  display flex
  align-items center
  gap 12px
  padding 10px 12px
  border-radius 9px
  cursor pointer

  &:hover
    background #f3f5fa

  &.selected
    background #e8edf8
    box-shadow inset 0 0 0 1px #b9c6e6

    .radio
      border-color THEME_COLOR_PRIMARY
      background radial-gradient(THEME_COLOR_PRIMARY 45%, white 50%)

.radio
  flex 0 0 16px
  width 16px
  height 16px
  border 2px solid #b8c1d6
  border-radius 50%
  background white

.candidate-main
  flex 1
  min-width 0

.candidate-top
  display flex
  align-items baseline
  gap 10px

  strong
    flex 0 0 auto
    max-width 45%
    overflow hidden
    white-space nowrap
    text-overflow ellipsis

.candidate-label
  min-width 0
  overflow hidden
  white-space nowrap
  text-overflow ellipsis
  font-size 13px
  color #6f7a93

.candidate-meta
  display flex
  align-items center
  flex-wrap wrap
  gap 4px 12px
  margin-top 4px
  font-size 12.5px
  color #6f7a93

  .partial
    color #9a6b00

.candidate-amount
  display flex
  flex-direction column
  align-items flex-end
  white-space nowrap

  strong
    font-size 15px

  span
    font-size 11.5px
    color #8a93a8

.empty
  margin 0
  padding 18px 12px
  border 1px dashed #d6dcea
  border-radius 12px
  text-align center
  font-size 13.5px
  color #8a93a8

.hint
  margin 0
  font-size 12.5px
  color #8a93a8

.button-container
  display flex
  flex-direction row-reverse
  justify-content flex-start
  gap 10px
  margin-top 12px

  .button
    min-width 110px
</style>

<style lang="stylus">
.link-purchase-modal
  > .modal
    width 640px !important
</style>
